/**
 * The KPI report as a PDF, built in the browser from the JSON of
 * POST /api/analytics/kpi.
 *
 * AUDIT-F17: ClientDashboard and WeeklySummaryCard downloaded that JSON
 * wrapped in a Blob typed application/pdf, so the ".pdf" file could not be
 * opened. The server answers JSON (documented in the OpenAPI contract);
 * the PDF is made here with jsPDF, as ExportReports already does.
 */
export interface KpiReportRow {
  callDate: string;
  outcome: string;
  prospectName: string;
  prospectCompany: string;
  agent: string;
  source: string;
  durationSeconds: number;
}

export interface KpiReport {
  total: number;
  connected: number;
  meetings: number;
  connectRate: number;
  convRate: number;
  bySource: Record<string, number>;
  byAgent: Record<string, number>;
  rows: KpiReportRow[];
}

export interface KpiReportOptions {
  from?: string | Date | null;
  to?: string | Date | null;
  source?: string | null;
  /** react-i18next translate function. */
  t: (key: string, options?: Record<string, unknown>) => string;
  /** BCP 47 locale for the dates (currentLocale() of '@/i18n/locale'). */
  locale: string;
  fileName: string;
}

const P = 'pages:analytics.exportReports.pdf';

function formatDate(value: string | Date | null | undefined, locale: string, withTime = false): string {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(locale, {
    day: '2-digit', month: '2-digit', year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  });
}

export async function downloadKpiReportPdf(report: KpiReport, options: KpiReportOptions): Promise<void> {
  const { jsPDF } = await import('jspdf');
  const { t } = options;
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const MARGIN = 18;
  let y = MARGIN;

  const line = (text: string, size = 10, bold = false) => {
    if (y > 280) {
      doc.addPage();
      y = MARGIN;
    }
    doc.setFontSize(size);
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.text(text, MARGIN, y);
    y += size * 0.55;
  };

  line(t(`${P}.reportTitle`), 16, true);
  line(t(`${P}.generatedOn`, { date: formatDate(new Date(), options.locale, true) }), 9);
  y += 4;

  line(t(`${P}.reportSettings`), 12, true);
  const period = [formatDate(options.from, options.locale), formatDate(options.to, options.locale)].filter(Boolean).join(' - ');
  if (period) line(`${t(`${P}.period`)} ${period}`);
  line(`${t(`${P}.listFilter`)} ${!options.source || options.source === 'all' ? t(`${P}.allLists`) : options.source}`);
  y += 4;

  line(t(`${P}.callsSummary`), 12, true);
  line(`${t(`${P}.totalCalls`)} ${report.total}`);
  line(`${t(`${P}.connectedContacts`)} ${report.connected} (${report.connectRate}%)`);
  line(`${t(`${P}.meetingsBooked`)} ${report.meetings} (${report.convRate}%)`);
  y += 4;

  line(t(`${P}.agentPerformance`), 12, true);
  for (const [agent, calls] of Object.entries(report.byAgent).sort((a, b) => b[1] - a[1])) {
    line(`${agent}: ${calls} ${t(`${P}.columns.calls`).toLowerCase()}`);
  }
  y += 4;

  line(t(`${P}.recentCallDetails`, { shown: report.rows.length, total: report.total }), 12, true);
  for (const row of report.rows) {
    const cells = [
      formatDate(row.callDate, options.locale, true),
      row.prospectName,
      row.prospectCompany,
      row.agent,
      row.outcome,
      `${Math.round(row.durationSeconds)}s`,
    ].filter((cell) => cell !== '');
    line(cells.join('  |  '), 8);
  }

  doc.save(options.fileName);
}
